import { useState, useEffect } from 'react';
import {
  HoldingsSubscription,
  HoldingsCostCategory,
  HOLDINGS_SUBSCRIPTION_STATUSES,
  HOLDINGS_BILLING_CYCLES
} from '../../types';
import { Button, Input, Select, Textarea } from "@repo/o37-ui";
import { X } from "lucide-react";

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

interface SubscriptionFormProps {
  subscription?: HoldingsSubscription | null;
  onClose: () => void;
  onSaved: () => void;
}

export default function SubscriptionForm({ subscription, onClose, onSaved }: SubscriptionFormProps) {
  const isEditing = !!subscription;

  const [vendorName, setVendorName] = useState(subscription?.vendorName || '');
  const [description, setDescription] = useState(subscription?.description || '');
  const [costDollars, setCostDollars] = useState(
    subscription ? (subscription.costCents / 100).toString() : ''
  );
  const [billingCycle, setBillingCycle] = useState(subscription?.billingCycle || 'monthly');
  const [renewalDate, setRenewalDate] = useState(subscription?.renewalDate?.split('T')[0] || '');
  const [startDate, setStartDate] = useState(subscription?.startDate?.split('T')[0] || '');
  const [totalSeats, setTotalSeats] = useState(subscription?.totalSeats?.toString() || '0');
  const [category, setCategory] = useState(subscription?.category || '');
  const [status, setStatus] = useState(subscription?.status || 'active');
  const [autoRenew, setAutoRenew] = useState(subscription?.autoRenew !== false);
  const [contractUrl, setContractUrl] = useState(subscription?.contractUrl || '');
  const [notes, setNotes] = useState(subscription?.notes || '');

  const [categories, setCategories] = useState<HoldingsCostCategory[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const userId = localStorage.getItem('app_user_id') || '';
  const userRole = localStorage.getItem('app_role') || 'user';

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/holdings/categories`, {
        headers: { 'x-user-id': userId, 'x-user-role': userRole }
      });
      if (response.ok) {
        const data = await response.json();
        setCategories(data.map((c: any) => ({
          id: c.id,
          name: c.name,
          description: c.description,
          color: c.color,
          isActive: c.is_active,
          sortOrder: c.sort_order
        })));
      }
    } catch (e) {
      console.error('Failed to load categories:', e);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!vendorName.trim()) {
      setError('Vendor name is required');
      return;
    }
    if (!costDollars || parseFloat(costDollars) < 0) {
      setError('Cost must be a valid positive number');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const payload = {
        vendorName: vendorName.trim(),
        description: description.trim() || null,
        costCents: Math.round(parseFloat(costDollars) * 100),
        billingCycle,
        renewalDate: renewalDate || null,
        startDate: startDate || null,
        totalSeats: parseInt(totalSeats) || 0,
        category: category || null,
        status,
        autoRenew,
        contractUrl: contractUrl.trim() || null,
        notes: notes.trim() || null
      };

      const url = isEditing
        ? `${API_URL}/holdings/subscriptions/${subscription.id}`
        : `${API_URL}/holdings/subscriptions`;

      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
          'x-user-role': userRole
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        onSaved();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to save subscription');
      }
    } catch (e) {
      setError('Failed to save subscription');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-100">
          <h2 className="text-xl font-semibold">
            {isEditing ? 'Edit Subscription' : 'Add Subscription'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-neutral-100 rounded-lg">
            <X className="w-5 h-5 text-neutral-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="p-3 bg-red-50 text-red-600 text-sm rounded-lg border border-red-200">
              {error}
            </div>
          )}

          {/* Vendor & Status */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Vendor Name *
              </label>
              <Input
                value={vendorName}
                onChange={(e) => setVendorName(e.target.value)}
                placeholder="e.g., Slack, AWS, GitHub"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Status
              </label>
              <Select value={status} onChange={(e) => setStatus(e.target.value as any)}>
                {HOLDINGS_SUBSCRIPTION_STATUSES.map(s => (
                  <option key={s.status} value={s.status}>{s.label}</option>
                ))}
              </Select>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Description
            </label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of the subscription"
            />
          </div>

          {/* Cost & Billing */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Cost *
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400">$</span>
                <Input
                  value={costDollars}
                  onChange={(e) => setCostDollars(e.target.value)}
                  placeholder="99.00"
                  type="number"
                  step="0.01"
                  min="0"
                  required
                  className="pl-7"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Billing Cycle
              </label>
              <Select value={billingCycle} onChange={(e) => setBillingCycle(e.target.value as any)}>
                {HOLDINGS_BILLING_CYCLES.map(c => (
                  <option key={c.cycle} value={c.cycle}>{c.label}</option>
                ))}
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Total Seats
              </label>
              <Input
                value={totalSeats}
                onChange={(e) => setTotalSeats(e.target.value)}
                placeholder="0"
                type="number"
                min="0"
              />
            </div>
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Category
            </label>
            <Select value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">Select category...</option>
              {categories.filter(c => c.isActive).map(c => (
                <option key={c.id} value={c.name}>{c.name}</option>
              ))}
            </Select>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Start Date
              </label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Renewal Date
              </label>
              <Input
                type="date"
                value={renewalDate}
                onChange={(e) => setRenewalDate(e.target.value)}
              />
            </div>
          </div>

          {/* Auto-renew */}
          <div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={autoRenew}
                onChange={(e) => setAutoRenew(e.target.checked)}
                className="rounded border-neutral-300"
              />
              <span className="font-medium text-neutral-700">Auto-renew</span>
            </label>
          </div>

          {/* Contract URL */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Contract URL
            </label>
            <Input
              value={contractUrl}
              onChange={(e) => setContractUrl(e.target.value)}
              placeholder="https://..."
              type="url"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Notes
            </label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional notes about this subscription..."
              rows={3}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-neutral-100">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving} text="Cancel" />
            <Button type="submit" disabled={saving} text={saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Add Subscription'} />
          </div>
        </form>
      </div>
    </div>
  );
}
